import { z } from 'zod';
import { Ownership, Sentiment, TimestampLike } from './common.js';
import { canonicalUrl } from '../lib/urlIdentity.js';

export const OrganicResult = z.object({
  // Not capped at 100. Our own DataForSEO pulls are depth-100, but imported
  // Semrush history goes deeper — 20 observations in the pilot export sit
  // between 101 and 119. Capping here would have rejected them at write time,
  // or worse, dropped them silently the way the older backfill importer did.
  position: z.number().int().min(1).max(250),
  /**
   * Rank among ALL SERP elements, ads and PAA included. Provenance only —
   * `position` is the organic rank and is what everything scores on. Absent on
   * anything captured before 2026-08-15, when `position` itself still carried
   * this value.
   */
  absolutePosition: z.number().int().min(1).optional(),
  url: z.string().url(),
  domain: z.string().min(1),
  title: z.string().default(''),
  snippet: z.string().default(''),
  // Denormalized classification — frozen at write time. Reclassification
  // does NOT retroactively update history (see §7.8).
  ownership: Ownership.optional(),
  sentiment: Sentiment.optional(),
  /**
   * False when this result is about someone else with the subject's name.
   * Absent means it is our subject — see TrackedUrlDoc.isSubject. Denormalized
   * alongside ownership and sentiment so every surface reading a stored pull
   * sees the same judgement.
   */
  isSubject: z.boolean().optional(),
  trackedUrlId: z.string().optional(),
  // How Google displayed the result, for View SERP's rebuilt view. Captured
  // from 2026-10-07; older pulls simply lack them and render plainer.
  /** Google results page the row was on (1-based), from DataForSEO's `page`. */
  page: z.number().int().optional(),
  /** The site name Google shows above the URL ("Crunchbase"), not the domain. */
  websiteName: z.string().optional(),
  /** Google's own URL line ("https://www.crunchbase.com › person › aaron-ipour"). */
  breadcrumb: z.string().optional(),
  /** Words Google bolded in the snippet. */
  highlighted: z.array(z.string()).optional(),
  preSnippet: z.string().optional(),
  /** Publication date Google printed before the snippet, YYYY-MM-DD. */
  date: z.string().optional(),
  rating: z
    .object({ value: z.number(), votes: z.number().optional(), max: z.number().optional() })
    .optional(),
  sitelinks: z
    .array(z.object({ title: z.string(), url: z.string(), description: z.string().optional() }))
    .optional(),
  /** Google ended the snippet with its "Read more" jump-to-text link. */
  readMore: z.boolean().optional(),
  imageUrl: z.string().optional(),
});
export type OrganicResult = z.infer<typeof OrganicResult>;

/** Google's AI Overview, when the page had one (captured from 2026-10-07). */
export const AiOverview = z.object({
  position: z.number().int().nullable(),
  markdown: z.string(),
  references: z.array(
    z.object({
      url: z.string(),
      title: z.string().optional(),
      domain: z.string().optional(),
      source: z.string().optional(),
    }),
  ),
});
export type AiOverview = z.infer<typeof AiOverview>;

export const KnowledgePanelResult = z.object({
  title: z.string(),
  description: z.string().optional(),
  url: z.string().url().optional(),
  attributes: z.record(z.string()).optional(),
});
export type KnowledgePanelResult = z.infer<typeof KnowledgePanelResult>;

export const PAAResult = z.object({
  question: z.string(),
  answer: z.string().optional(),
  sourceUrl: z.string().url().optional(),
});
export type PAAResult = z.infer<typeof PAAResult>;

/**
 * A non-organic SERP block — video carousel, top stories, images, local pack,
 * X/Twitter, related searches.
 *
 * Asked for in the 2026-08-03 walkthrough: the reconstruction should show
 * "the Knowledge Panel and other modules, like short videos… to make it look
 * as much as possible" like the real results page. DataForSEO returns all of
 * these from the `advanced` endpoint already; until v3.12.0 the parser threw
 * every one of them away.
 *
 * Normalized rather than typed per module: the shapes differ (local_pack is a
 * single entity, related_searches is a list of strings, video/top_stories/
 * images carry nested items), and a normalized row renders generically while
 * staying tolerant of fields DataForSEO adds or omits.
 */
export const SerpModuleItem = z.object({
  title: z.string().default(''),
  url: z.string().optional(),
  source: z.string().optional(),
  domain: z.string().optional(),
  date: z.string().optional(),
  imageUrl: z.string().optional(),
  description: z.string().optional(),
});
export type SerpModuleItem = z.infer<typeof SerpModuleItem>;

export const SerpModule = z.object({
  /** DataForSEO item type verbatim — 'video', 'top_stories', 'images', … */
  type: z.string().min(1),
  /**
   * `rank_absolute` on the SERP, so the capture view can interleave the block
   * where Google actually put it. Null when DataForSEO omits a rank.
   */
  position: z.number().int().nullable().default(null),
  /** Present on single-entity blocks such as local_pack. */
  title: z.string().optional(),
  items: z.array(SerpModuleItem).max(20).default([]),
});
export type SerpModule = z.infer<typeof SerpModule>;

/**
 * Which device the SERP was crawled on.
 *
 * A tracked target is really (location × device) — Semrush models it that way,
 * and DataForSEO can crawl either. Until the Semrush migration everything here
 * was desktop, implicitly and unrecorded.
 *
 * ⚠️ Defaults to 'desktop' rather than being optional, and every existing
 * ranking doc is backfilled with it, because Firestore equality filters DO NOT
 * MATCH MISSING FIELDS — a `where('device','==','desktop')` would silently skip
 * every document written before this field existed.
 */
export const Device = z.enum(['desktop', 'mobile', 'tablet']);
export type Device = z.infer<typeof Device>;

/**
 * Ranking doc id is `${date}_${locationCode}` for desktop and
 * `${date}_${locationCode}_${device}` otherwise.
 *
 * Desktop deliberately keeps the original unsuffixed form: it is 100% of
 * existing data and of the Semrush pilot, so nothing has to be re-keyed and no
 * behaviour changes today. Non-desktop targets get a distinct key the moment
 * they appear, which is what keeps two devices for one city from overwriting
 * each other.
 */
export function rankingDocId(date: string, locationCode: number, device: Device = 'desktop') {
  return device === 'desktop'
    ? `${date}_${locationCode}`
    : `${date}_${locationCode}_${device}`;
}

/**
 * Provenance for one pull: what we asked DataForSEO for, and what came back.
 *
 * Exists because a thin SERP was undiagnosable after the fact — 13 organic
 * rows against a depth of 100 could equally be Google truncating the page or
 * us dropping results, and nothing recorded which. `seResultsCount` well above
 * `itemsCount` is the truncation signature.
 */
/**
 * A PM's ruling on a capture flagged as divergent. 'accurate' puts it back into
 * every chart, score and report; 'wrong' keeps it out for good. A wrong capture
 * cannot be fixed by re-pulling — a pull only ever replaces today's document —
 * so without the second verdict it would block every report covering its date.
 */
export const CaptureReview = z.object({
  verdict: z.enum(['accurate', 'wrong']),
  by: z.string(),
  byEmail: z.string().nullable().optional(),
  /** ISO timestamp. */
  at: z.string(),
});
export type CaptureReview = z.infer<typeof CaptureReview>;

/** Why a capture is not trusted until someone looks at it. See isDivergentCapture. */
export const SuspectCapture = z.object({
  reason: z.literal('diverged'),
  /** How many of this capture's top results also appeared in the reference. */
  sharedTopResults: z.number().int(),
  /** Ranking doc id of the last trusted capture it was compared against. */
  comparedWith: z.string(),
  review: CaptureReview.optional(),
});
export type SuspectCapture = z.infer<typeof SuspectCapture>;

export const SerpMeta = z.object({
  depth: z.number().int(),
  /** Pages the crawler was allowed to walk. depth alone does not paginate. */
  maxCrawlPages: z.number().int().optional(),
  /** Extra Google query-string params sent with the request, e.g. '&filter=0'. */
  searchParam: z.string().optional(),
  /** DataForSEO task id — the handle their support needs to trace a bad pull. */
  taskId: z.string().nullable().optional(),
  itemsCount: z.number().int().nullable(),
  seResultsCount: z.number().int().nullable(),
  /** Result pages actually crawled. Low against a high depth = truncated capture. */
  pagesCount: z.number().int().nullable().optional(),
  organicReturned: z.number().int(),
  /**
   * DataForSEO returned some pages and failed the rest (status 40106). Written
   * only when true; captures before 2026-10-21 never carry it, because a
   * partial result was thrown away rather than stored.
   */
  partial: z.boolean().optional(),
  suspect: SuspectCapture.optional(),
  /**
   * Every attempt when there was more than one (performPull retries a capture
   * that looks truncated or diverged). Shows how often the provider disagrees
   * with itself for the same request.
   */
  attempts: z
    .array(
      z.object({
        taskId: z.string().nullable(),
        pagesCount: z.number().int().nullable(),
        organicReturned: z.number().int(),
        seResultsCount: z.number().int().nullable(),
        sharedTopResults: z.number().int().nullable(),
      }),
    )
    .optional(),
  /**
   * A second provider's page 1, asked only when every attempt diverged from
   * the last trusted capture. 'confirmed': it matched one of the attempts, so
   * the SERP really changed and that attempt was written as trusted.
   * 'disagreed': it matched none (often it matched the old capture instead,
   * so the provider was wrong), and the capture stays flagged. 'unavailable':
   * it could not be reached or returned too little to judge.
   */
  secondOpinion: z
    .object({
      provider: z.literal('scraperapi'),
      verdict: z.enum(['confirmed', 'disagreed', 'unavailable']),
      refereeResults: z.number().int().optional(),
      /** Referee results found in the written capture's top 10. */
      agreedWithCapture: z.number().int().optional(),
      /** Referee results found in the last trusted capture's top 10. */
      agreedWithReference: z.number().int().optional(),
    })
    .optional(),
});

/**
 * Share of Google's own reported result total we must have captured before a
 * short crawl counts as the whole SERP rather than a truncated one.
 *
 * Calibrated against every stored capture the page/depth test flags
 * (2026-08-21, 10 of 78). The two populations separate on this ratio: captures
 * that walked 4 pages against a reported 34–65 results land at 0.60–1.06 —
 * Google ran out of results, which is what a narrow or exact-phrase query
 * looks like — while the genuine truncations stopped at page 1 or 2 while
 * claiming thousands of results, at 0.00–0.27.
 */
const WHOLE_SERP_COVERAGE = 0.6;

/**
 * Whether a capture looks truncated rather than complete.
 *
 * DataForSEO confirmed 2026-08-06 that `depth` and `max_crawl_pages` are upper
 * limits — the crawl stops when Google stops offering pages — so a capture can
 * be short without any error being raised. For `ryan mccorvie` their crawler
 * saw 2 pages and 13 organic results while a browser in the same metro
 * paginated to 14 pages and showed the client's negative coverage at ~#14.
 *
 * Deliberately conservative: only flags when the crawler stopped well before
 * what was asked for. Nothing downstream should treat a flagged capture as a
 * complete picture of a SERP.
 *
 * `seResultsCount` is usable only as an exoneration, never as an accusation:
 * it counts documents rather than organic rows and inflates wildly on broad
 * queries (1,370 for `scott tom` against the 97 rows Google actually served).
 * But when it comes back small, a short crawl is the SERP being short. Quoting
 * a keyword to disambiguate a common name is the case that exposed this:
 * `"Scott Tom"` returned 40 rows over 4 pages against a reported 57 results —
 * every page Google offered — and every surface told the PM not to report on
 * it.
 */
export function isTruncatedCapture(meta: SerpMeta | undefined): boolean {
  if (!meta) return false; // pre-2026-08-06 docs carry no provenance either way
  const requestedPages = meta.maxCrawlPages ?? Math.ceil(meta.depth / 10);
  if (meta.pagesCount == null) return false;
  if (meta.pagesCount >= requestedPages) return false;
  if (meta.organicReturned >= meta.depth * 0.5) return false;
  // A big seResultsCount cannot exonerate anything here: clearing this bar
  // against one would take more rows than the depth test just rejected.
  if (
    meta.seResultsCount != null &&
    meta.seResultsCount > 0 &&
    meta.organicReturned >= meta.seResultsCount * WHOLE_SERP_COVERAGE
  ) {
    return false;
  }
  return true;
}
export type SerpMeta = z.infer<typeof SerpMeta>;

/** Results compared from the top of each capture. */
export const DIVERGENCE_TOP_N = 10;
/** At most this many shared results between two captures means different SERPs. */
export const DIVERGENCE_MAX_SHARED = 1;
/** Below this many rows on either side there is too little to compare. */
const DIVERGENCE_MIN_ROWS = 5;

/**
 * How many of the top results two captures share, by canonical URL, or null
 * when either is too short to compare.
 */
export function sharedTopResults(
  fresh: ReadonlyArray<{ url: string; position: number }>,
  reference: ReadonlyArray<{ url: string; position: number }>,
): number | null {
  const top = (rows: ReadonlyArray<{ url: string; position: number }>) =>
    [...rows]
      .sort((a, b) => a.position - b.position)
      .slice(0, DIVERGENCE_TOP_N)
      .map((r) => canonicalUrl(r.url));
  const a = top(fresh);
  const b = new Set(top(reference));
  if (a.length < DIVERGENCE_MIN_ROWS || b.size < DIVERGENCE_MIN_ROWS) return null;
  return a.filter((u) => b.has(u)).length;
}

/**
 * Whether a capture is a different SERP from the last trusted one.
 *
 * DataForSEO can hand back a complete, plausible-looking capture for what is
 * not the query we asked about. On 2026-09-25 "sky dayton" came back with 97
 * rows over all 10 pages — a Dayton Flyers livestream, 311 Sky Dr in Dayton TN,
 * a Somali .gov.so net-worth spam page — and not one of Wikipedia, his own
 * site or Craft Ventures, which a browser showed in the top four. Nothing
 * tripped isTruncatedCapture, because nothing was short.
 *
 * Calibrated over every consecutive pair of captures from 2026-08-06 to
 * 2026-09-28 (556 pairs): before 09-25 only one keyword, the Chinese-script
 * "徵 周", ever shared ≤1 of its top 10 with the week before. From 09-25 to
 * 09-28, 23 captures shared ≤1 with the last trusted one
 * (scripts/flagDivergentCaptures.ts). se_results_count is no use as the test: it already
 * swung between ~100 and millions through August on captures that were fine.
 *
 * Real SERPs can change this much — a breaking story — which is why this
 * flags for review rather than discarding. A genuine shift is reviewed once;
 * after that the accepted capture is the reference and the next week matches it.
 */
export function isDivergentCapture(
  fresh: ReadonlyArray<{ url: string; position: number }>,
  reference: ReadonlyArray<{ url: string; position: number }>,
): boolean {
  const shared = sharedTopResults(fresh, reference);
  return shared != null && shared <= DIVERGENCE_MAX_SHARED;
}

/**
 * Whether a capture must be kept out of charts, scores, standings and reports:
 * flagged as divergent and not since accepted by a PM.
 */
export function isUntrustedCapture(meta: SerpMeta | undefined | null): boolean {
  return meta?.suspect != null && meta.suspect.review?.verdict !== 'accurate';
}

/** Flagged and nobody has ruled on it yet — blocks publishing a report. */
export function captureNeedsReview(meta: SerpMeta | undefined | null): boolean {
  return meta?.suspect != null && meta.suspect.review == null;
}

export const RankingDoc = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  locationCode: z.number().int(),
  locationName: z.string().min(1),
  device: Device.default('desktop'),
  pulledAt: TimestampLike,
  organic: z.array(OrganicResult).max(100),
  knowledgePanel: KnowledgePanelResult.optional(),
  paa: z.array(PAAResult).optional(),
  /** Non-organic blocks, in SERP order. Capped to protect the 1 MiB doc. */
  modules: z.array(SerpModule).max(24).optional(),
  aiOverview: AiOverview.optional(),
  /** Where People also ask sat among all page elements; places it in the rebuild. */
  paaPosition: z.number().int().optional(),
  /**
   * What the SERP reported for this pull. Optional because every doc written
   * before 2026-08-06 predates it — and per the `device` note above, readers
   * must not filter on it with a Firestore equality.
   */
  serpMeta: SerpMeta.optional(),
  /**
   * The most recent re-pull that was rejected for collapsing — see
   * THIN_PULL_RATIO in performPull. Present only when one has been rejected,
   * and deliberately a single field rather than a growing array: Cloud Logging
   * keeps the full run history.
   */
  lastThinPull: SerpMeta.extend({
    at: TimestampLike,
    /** Rows on the capture that was kept instead. */
    keptOrganicCount: z.number().int(),
  }).optional(),
  hasOverflow: z.boolean().default(false), // set when >30 results spill to subcollection
});
export type RankingDoc = z.infer<typeof RankingDoc>;
